// Tenant Assets / Projects / Bookings — lean MVP CRUD pages
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, Briefcase, FolderKanban, CalendarRange, Pencil, Check } from "lucide-react";
import { api } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader } from "../products/_shared";
import { fmtMoney as fmt } from "../../../lib/currency";
import { useConfirm } from "../../../components/ConfirmDialog";

// ────────────────────────── ASSETS ──────────────────────────
export function AssetsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const confirm = useConfirm();
  const load = () => api.get("/t/assets").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const openCreate = () => { setForm({ name: "", code: "", category: "", status: "active", purchase_value: 0, current_value: 0 }); setEdit(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    if (!(await confirm({ title: edit ? "Save changes?" : "Create asset?", message: `${edit ? "Update" : "Add"} "${form.name}" to your asset register.` }))) return;
    try {
      if (edit) await api.patch(`/t/assets/${edit.id}`, form);
      else await api.post("/t/assets", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!(await confirm({ title: "Delete asset?", message: `"${r.name}" will be permanently removed.`, tone: "danger", yesLabel: "Delete" }))) return;
    await api.delete(`/t/assets/${r.id}`); toast.success("Deleted"); load();
  };
  const statusColor = (s) => ({ active: "bg-emerald-100 text-emerald-700", maintenance: "bg-amber-100 text-amber-700", retired: "bg-black/5 text-black/55", disposed: "bg-rose-100 text-rose-700" })[s];
  return (
    <div data-testid="assets-page" className="p-6 lg:p-8">
      <PageHeader title="Assets" breadcrumb="Assets" actions={
        <PrimaryButton icon={Plus} onClick={openCreate} testid="assets-new">New Asset</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr><th className="text-left p-3.5">Code</th><th className="text-left p-3.5">Name</th><th className="text-left p-3.5">Category</th><th className="text-left p-3.5">Location</th><th className="text-right p-3.5">Value</th><th className="text-left p-3.5">Status</th><th className="text-right p-3.5">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5 font-mono">{r.code || "—"}</td>
                <td className="p-3.5 font-semibold">{r.name}</td>
                <td className="p-3.5">{r.category || "—"}</td>
                <td className="p-3.5">{r.location || "—"}</td>
                <td className="p-3.5 text-right font-semibold">{fmt(r.current_value || r.purchase_value)}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${statusColor(r.status)}`}>{r.status}</span></td>
                <td className="p-3.5 text-right">
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-black/5 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="p-10 text-center text-black/45">No assets yet</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">{edit ? "Edit" : "New"} Asset</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Field lbl="Asset Name" required><TextInput value={form.name} onChange={(v) => setForm((x) => ({ ...x, name: v }))} testid="asset-f-name" /></Field>
              <Field lbl="Code"><TextInput value={form.code} onChange={(v) => setForm((x) => ({ ...x, code: v }))} placeholder="FA-0001" /></Field>
              <Field lbl="Category"><TextInput value={form.category} onChange={(v) => setForm((x) => ({ ...x, category: v }))} placeholder="Equipment / Furniture" /></Field>
              <Field lbl="Location"><TextInput value={form.location} onChange={(v) => setForm((x) => ({ ...x, location: v }))} /></Field>
              <Field lbl="Assigned To"><TextInput value={form.assigned_to} onChange={(v) => setForm((x) => ({ ...x, assigned_to: v }))} /></Field>
              <Field lbl="Purchase Date"><TextInput type="date" value={form.purchase_date} onChange={(v) => setForm((x) => ({ ...x, purchase_date: v }))} /></Field>
              <Field lbl="Purchase Value"><TextInput type="number" value={form.purchase_value} onChange={(v) => setForm((x) => ({ ...x, purchase_value: Number(v) }))} /></Field>
              <Field lbl="Current Value"><TextInput type="number" value={form.current_value} onChange={(v) => setForm((x) => ({ ...x, current_value: Number(v) }))} /></Field>
              <Field lbl="Status"><Select value={form.status} onChange={(v) => setForm((x) => ({ ...x, status: v }))} options={["active", "maintenance", "retired", "disposed"].map((s) => ({ value: s, label: s }))} /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="asset-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── PROJECTS (with task board) ──────────────────────────
export function ProjectsPage() {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [drilledProject, setDrilled] = useState(null);
  const confirm = useConfirm();
  const load = () => api.get("/t/projects").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const openCreate = () => { setForm({ name: "", client: "", status: "active", budget: 0, start_date: new Date().toISOString().slice(0, 10) }); setEdit(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    if (!(await confirm({ title: edit ? "Save project?" : "Create project?", message: form.name }))) return;
    try {
      if (edit) await api.patch(`/t/projects/${edit.id}`, form);
      else await api.post("/t/projects", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!(await confirm({ title: "Delete project?", message: `"${r.name}" and all its tasks will be removed.`, tone: "danger", yesLabel: "Delete" }))) return;
    await api.delete(`/t/projects/${r.id}`); toast.success("Deleted"); load();
  };
  const colorFor = (s) => ({ planning: "bg-sky-100 text-sky-700", active: "bg-emerald-100 text-emerald-700", "on-hold": "bg-amber-100 text-amber-700", completed: "bg-violet-100 text-violet-700", cancelled: "bg-rose-100 text-rose-700" })[s];

  if (drilledProject) return <ProjectTasks project={drilledProject} onBack={() => { setDrilled(null); load(); }} />;

  return (
    <div data-testid="projects-page" className="p-6 lg:p-8">
      <PageHeader title="Projects" breadcrumb="Projects" actions={
        <PrimaryButton icon={Plus} onClick={openCreate} testid="projects-new">New Project</PrimaryButton>
      } />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => {
          const pct = r.task_count ? Math.round((r.task_done / r.task_count) * 100) : 0;
          return (
            <Card key={r.id} className="p-5 hover:border-[#1FA868]/40 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-extrabold text-base truncate">{r.name}</h3>
                  <div className="text-xs text-black/55 mt-0.5">{r.client || "—"}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${colorFor(r.status)}`}>{r.status}</span>
              </div>
              <div className="text-xs text-black/55">Budget · <span className="font-bold text-black">{fmt(r.budget)}</span></div>
              <div className="mt-4">
                <div className="flex justify-between text-[11px] text-black/55"><span>Progress</span><span>{r.task_done || 0}/{r.task_count || 0}</span></div>
                <div className="h-1.5 bg-black/5 rounded-full mt-1 overflow-hidden"><div className="h-full bg-[#1FA868]" style={{ width: `${pct}%` }} /></div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setDrilled(r)} data-testid={`project-open-${r.id}`} className="flex-1 px-3 py-1.5 rounded-lg bg-[#E8F5E9] text-[#1FA868] font-semibold text-xs hover:bg-[#D9F0DF]">Open Tasks</button>
                <button onClick={() => openEdit(r)} className="px-2 py-1.5 rounded-lg border border-black/10"><Pencil size={12} /></button>
                <button onClick={() => del(r)} className="px-2 py-1.5 rounded-lg border border-rose-200 text-rose-600"><Trash2 size={12} /></button>
              </div>
            </Card>
          );
        })}
        {!rows.length && <Card className="p-10 text-center text-black/45 col-span-full">No projects yet</Card>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">{edit ? "Edit" : "New"} Project</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Field lbl="Name" required className="col-span-2"><TextInput value={form.name} onChange={(v) => setForm((x) => ({ ...x, name: v }))} testid="project-f-name" /></Field>
              <Field lbl="Client"><TextInput value={form.client} onChange={(v) => setForm((x) => ({ ...x, client: v }))} /></Field>
              <Field lbl="Budget"><TextInput type="number" value={form.budget} onChange={(v) => setForm((x) => ({ ...x, budget: Number(v) }))} /></Field>
              <Field lbl="Start"><TextInput type="date" value={form.start_date} onChange={(v) => setForm((x) => ({ ...x, start_date: v }))} /></Field>
              <Field lbl="End"><TextInput type="date" value={form.end_date} onChange={(v) => setForm((x) => ({ ...x, end_date: v }))} /></Field>
              <Field lbl="Status"><Select value={form.status} onChange={(v) => setForm((x) => ({ ...x, status: v }))} options={["planning", "active", "on-hold", "completed", "cancelled"].map((s) => ({ value: s, label: s }))} /></Field>
              <Field lbl="Description" className="col-span-2"><textarea value={form.description || ""} onChange={(e) => setForm((x) => ({ ...x, description: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[80px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="project-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectTasks({ project, onBack }) {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const confirm = useConfirm();
  const load = () => api.get(`/t/projects/${project.id}/tasks`).then((r) => setTasks(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const submit = async () => {
    if (!(await confirm({ title: "Add task?", message: form.title }))) return;
    await api.post(`/t/projects/${project.id}/tasks`, form);
    toast.success("Task added"); setOpen(false); setForm({}); load();
  };
  const move = async (t, status) => {
    await api.patch(`/t/projects/${project.id}/tasks/${t.id}`, { ...t, status });
    load();
  };
  const del = async (t) => {
    if (!(await confirm({ title: "Delete task?", tone: "danger", yesLabel: "Delete" }))) return;
    await api.delete(`/t/projects/${project.id}/tasks/${t.id}`); load();
  };
  const cols = ["todo", "in-progress", "review", "done"];
  return (
    <div data-testid="project-tasks-page" className="p-6 lg:p-8">
      <PageHeader title={project.name} breadcrumb={`Projects / ${project.name}`} actions={
        <>
          <button onClick={onBack} className="px-3 py-2 rounded-xl bg-white border border-black/10 text-sm font-semibold">Back to Projects</button>
          <PrimaryButton icon={Plus} onClick={() => { setForm({ title: "", priority: "medium", status: "todo" }); setOpen(true); }} testid="task-new">Add Task</PrimaryButton>
        </>
      } />
      <div className="grid md:grid-cols-4 gap-4">
        {cols.map((c) => (
          <Card key={c} className="p-4 bg-[#FAFBFB]">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-black/55 mb-3 flex justify-between">
              <span>{c}</span>
              <span className="bg-white px-2 rounded">{tasks.filter((t) => t.status === c).length}</span>
            </div>
            <div className="space-y-2">
              {tasks.filter((t) => t.status === c).map((t) => (
                <div key={t.id} className="bg-white rounded-lg p-3 border border-black/5 group">
                  <div className="font-semibold text-sm">{t.title}</div>
                  {t.assignee && <div className="text-[10px] text-black/55 mt-1">{t.assignee}</div>}
                  <div className="flex justify-between items-center mt-2">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${t.priority === "urgent" ? "bg-rose-100 text-rose-700" : t.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-black/5"}`}>{t.priority}</span>
                    <div className="hidden group-hover:flex gap-1">
                      {cols.filter((x) => x !== c).map((x) => (
                        <button key={x} onClick={() => move(t, x)} title={`Move to ${x}`} className="text-[9px] px-1.5 py-0.5 rounded bg-[#E8F5E9] text-[#1FA868] font-bold">{x[0].toUpperCase()}</button>
                      ))}
                      <button onClick={() => del(t)} className="text-rose-600"><Trash2 size={10} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {!tasks.filter((t) => t.status === c).length && <div className="text-center text-black/35 text-xs py-4">No tasks</div>}
            </div>
          </Card>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-black/5 flex items-center justify-between"><h3 className="font-bold">New Task</h3><button onClick={() => setOpen(false)}><X size={15} /></button></div>
            <div className="p-5 space-y-3">
              <Field lbl="Title" required><TextInput value={form.title} onChange={(v) => setForm((x) => ({ ...x, title: v }))} testid="task-f-title" /></Field>
              <Field lbl="Assignee"><TextInput value={form.assignee} onChange={(v) => setForm((x) => ({ ...x, assignee: v }))} /></Field>
              <Field lbl="Due Date"><TextInput type="date" value={form.due_date} onChange={(v) => setForm((x) => ({ ...x, due_date: v }))} /></Field>
              <Field lbl="Priority"><Select value={form.priority} onChange={(v) => setForm((x) => ({ ...x, priority: v }))} options={["low", "medium", "high", "urgent"].map((p) => ({ value: p, label: p }))} /></Field>
              <Field lbl="Description"><textarea value={form.description || ""} onChange={(e) => setForm((x) => ({ ...x, description: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="task-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── BOOKINGS ──────────────────────────
export function BookingsPage() {
  const [rows, setRows] = useState([]);
  const [resources, setResources] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [open, setOpen] = useState(false);
  const [openRes, setOpenRes] = useState(false);
  const [form, setForm] = useState({});
  const [resForm, setResForm] = useState({});
  const confirm = useConfirm();
  const load = () => Promise.all([
    api.get("/t/bookings").then((r) => setRows(r.data)),
    api.get("/t/booking-resources").then((r) => setResources(r.data)),
  ]);
  useEffect(() => { load(); }, []);
  const submitBooking = async () => {
    if (!(await confirm({ title: "Confirm booking?", message: `${form.customer_name} on ${(form.start_at || "").slice(0, 16).replace("T", " ")}` }))) return;
    try {
      const res = resources.find((r) => r.id === form.resource_id);
      await api.post("/t/bookings", { ...form, resource_name: res?.name || "" });
      toast.success("Booking created"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitResource = async () => {
    if (!(await confirm({ title: "Add resource?", message: resForm.name }))) return;
    await api.post("/t/booking-resources", resForm);
    toast.success("Resource added"); setOpenRes(false); load();
  };
  const updateStatus = async (b, status) => {
    if (!(await confirm({ title: `Mark ${status}?`, message: `Booking for ${b.customer_name}` }))) return;
    await api.patch(`/t/bookings/${b.id}`, { status });
    toast.success("Updated"); load();
  };
  const delBooking = async (b) => {
    if (!(await confirm({ title: "Delete booking?", tone: "danger", yesLabel: "Delete" }))) return;
    await api.delete(`/t/bookings/${b.id}`); load();
  };
  const delRes = async (r) => {
    if (!(await confirm({ title: "Delete resource?", tone: "danger", yesLabel: "Delete" }))) return;
    await api.delete(`/t/booking-resources/${r.id}`); load();
  };
  const colorFor = (s) => ({ pending: "bg-amber-100 text-amber-700", confirmed: "bg-emerald-100 text-emerald-700", cancelled: "bg-rose-100 text-rose-700", completed: "bg-violet-100 text-violet-700" })[s];

  return (
    <div data-testid="bookings-page" className="p-6 lg:p-8">
      <PageHeader title="Bookings" breadcrumb="Bookings" actions={
        tab === "bookings"
          ? <PrimaryButton icon={Plus} onClick={() => { setForm({ status: "pending", start_at: new Date().toISOString().slice(0, 16), end_at: new Date(Date.now() + 3600000).toISOString().slice(0, 16), amount: 0 }); setOpen(true); }} testid="booking-new">New Booking</PrimaryButton>
          : <PrimaryButton icon={Plus} onClick={() => { setResForm({ name: "", capacity: 1, hourly_rate: 0, active: true }); setOpenRes(true); }} testid="resource-new">New Resource</PrimaryButton>
      } />
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("bookings")} className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === "bookings" ? "bg-[#1FA868] text-white" : "bg-white border border-black/10"}`}>Bookings</button>
        <button onClick={() => setTab("resources")} className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === "resources" ? "bg-[#1FA868] text-white" : "bg-white border border-black/10"}`}>Resources</button>
      </div>

      {tab === "bookings" ? (
        <Card>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
              <tr><th className="text-left p-3.5">Resource</th><th className="text-left p-3.5">Customer</th><th className="text-left p-3.5">Start</th><th className="text-left p-3.5">End</th><th className="text-right p-3.5">Amount</th><th className="text-left p-3.5">Status</th><th className="text-right p-3.5">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {rows.map((b) => (
                <tr key={b.id}>
                  <td className="p-3.5 font-semibold">{b.resource_name || "—"}</td>
                  <td className="p-3.5"><div>{b.customer_name}</div><div className="text-[11px] text-black/55">{b.customer_email || b.customer_phone || ""}</div></td>
                  <td className="p-3.5">{(b.start_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3.5">{(b.end_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3.5 text-right font-semibold">{fmt(b.amount)}</td>
                  <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${colorFor(b.status)}`}>{b.status}</span></td>
                  <td className="p-3.5 text-right">
                    {b.status === "pending" && <button onClick={() => updateStatus(b, "confirmed")} data-testid={`booking-confirm-${b.id}`} className="text-[10px] font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-700 mr-1">Confirm</button>}
                    {b.status !== "completed" && b.status !== "cancelled" && <button onClick={() => updateStatus(b, "completed")} className="text-[10px] font-bold px-2 py-1 rounded bg-violet-100 text-violet-700 mr-1">Complete</button>}
                    <button onClick={() => delBooking(b)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="p-10 text-center text-black/45">No bookings</td></tr>}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
              <tr><th className="text-left p-3.5">Name</th><th className="text-left p-3.5">Description</th><th className="text-right p-3.5">Capacity</th><th className="text-right p-3.5">Rate/hr</th><th className="text-right p-3.5">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {resources.map((r) => (<tr key={r.id}><td className="p-3.5 font-semibold">{r.name}</td><td className="p-3.5">{r.description || "—"}</td><td className="p-3.5 text-right">{r.capacity}</td><td className="p-3.5 text-right">{fmt(r.hourly_rate)}</td><td className="p-3.5 text-right"><button onClick={() => delRes(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button></td></tr>))}
              {!resources.length && <tr><td colSpan={5} className="p-10 text-center text-black/45">No resources — create one to start booking</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="p-4 border-b border-black/5 flex items-center justify-between"><h3 className="font-bold">New Booking</h3><button onClick={() => setOpen(false)}><X size={15} /></button></div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Field lbl="Resource" className="col-span-2"><Select value={form.resource_id} onChange={(v) => setForm((x) => ({ ...x, resource_id: v }))} options={resources.map((r) => ({ value: r.id, label: r.name }))} testid="booking-f-resource" /></Field>
              <Field lbl="Customer Name" required className="col-span-2"><TextInput value={form.customer_name} onChange={(v) => setForm((x) => ({ ...x, customer_name: v }))} testid="booking-f-customer" /></Field>
              <Field lbl="Email"><TextInput value={form.customer_email} onChange={(v) => setForm((x) => ({ ...x, customer_email: v }))} /></Field>
              <Field lbl="Phone"><TextInput value={form.customer_phone} onChange={(v) => setForm((x) => ({ ...x, customer_phone: v }))} /></Field>
              <Field lbl="Start" required><TextInput type="datetime-local" value={form.start_at} onChange={(v) => setForm((x) => ({ ...x, start_at: v }))} testid="booking-f-start" /></Field>
              <Field lbl="End" required><TextInput type="datetime-local" value={form.end_at} onChange={(v) => setForm((x) => ({ ...x, end_at: v }))} testid="booking-f-end" /></Field>
              <Field lbl="Amount"><TextInput type="number" value={form.amount} onChange={(v) => setForm((x) => ({ ...x, amount: Number(v) }))} /></Field>
              <Field lbl="Status"><Select value={form.status} onChange={(v) => setForm((x) => ({ ...x, status: v }))} options={["pending", "confirmed", "completed", "cancelled"].map((s) => ({ value: s, label: s }))} /></Field>
              <Field lbl="Note" className="col-span-2"><textarea value={form.note || ""} onChange={(e) => setForm((x) => ({ ...x, note: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[60px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submitBooking} testid="booking-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
      {openRes && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-black/5 flex items-center justify-between"><h3 className="font-bold">New Resource</h3><button onClick={() => setOpenRes(false)}><X size={15} /></button></div>
            <div className="p-5 space-y-3">
              <Field lbl="Name" required><TextInput value={resForm.name} onChange={(v) => setResForm((x) => ({ ...x, name: v }))} testid="resource-f-name" /></Field>
              <Field lbl="Description"><textarea value={resForm.description || ""} onChange={(e) => setResForm((x) => ({ ...x, description: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" /></Field>
              <Field lbl="Capacity"><TextInput type="number" value={resForm.capacity} onChange={(v) => setResForm((x) => ({ ...x, capacity: Number(v) }))} /></Field>
              <Field lbl="Rate per hour"><TextInput type="number" value={resForm.hourly_rate} onChange={(v) => setResForm((x) => ({ ...x, hourly_rate: Number(v) }))} /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpenRes(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submitResource} testid="resource-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
